import { isObject } from '@scalar/helpers/object/is-object';
import { getResolvedRef } from '@scalar/workspace-store/helpers/get-resolved-ref';
import { getMarkdownExamples } from './get-markdown-examples.js';
const bookkeeping = new Set(['$ref', '$ref-value', '$global', '$status', '__scalar_']);
/** Sibling constraints make a reference a different example source from its target. */
const identity = (source) => {
    if (!isObject(source.schema)) {
        return undefined;
    }
    const schema = source.schema;
    const target = '$ref' in schema && Object.keys(schema).every((key) => bookkeeping.has(key)) ? getResolvedRef(schema) : schema;
    return isObject(target) ? target : schema;
};
/** Reuse generation by schema and context; deduplicate only after comparing the serialized example. */
export const createDocumentExamples = (anchors) => {
    const cache = new Map();
    const shown = new WeakMap();
    let cachedCount = 0;
    let nextId = 0;
    return {
        get: (...args) => {
            const [source, ...settings] = args;
            const schema = identity(source);
            if (!schema || source.example !== undefined || Object.keys(source.examples ?? {}).length) {
                return getMarkdownExamples(...args);
            }
            const scope = JSON.stringify(settings);
            const previous = cache.get(schema)?.get(scope);
            if (previous) {
                return previous;
            }
            // Large APIs must not retain every generated object until the export finishes.
            if (cachedCount >= 256) {
                cache.clear();
                cachedCount = 0;
            }
            const entries = cache.get(schema) ?? new Map();
            const examples = getMarkdownExamples(...args);
            entries.set(scope, examples);
            cache.set(schema, entries);
            cachedCount++;
            return examples;
        },
        show: (source, scope, code) => {
            const schema = identity(source);
            const entries = schema ? (shown.get(schema) ?? new Map()) : new Map();
            const previous = entries.get(scope);
            if (previous?.value === code.value) {
                return { id: previous.id, previous: true };
            }
            const id = anchors.get('example', String(++nextId));
            entries.set(scope, { id, value: code.value });
            if (schema) {
                shown.set(schema, entries);
            }
            return { id, previous: false };
        },
    };
};
